import { UserRole } from '../../auth/types/auth.types';
import { momentoMx } from '../../metas-colaborador/utils/formato';
import type {
  Dispositivo,
  EstadoDispositivo,
  MotivoCierre,
  ResultadoSonar,
  ResumenDispositivos,
  UltimoSonar,
} from '../types/dispositivos.types';

/* ------------------------------------------------------------------ roles -- */

/**
 * Quién entra a la pantalla (decisión del usuario, 13-oct; D12 de la spec):
 * TI y el gerente de la tienda. Las cuentas `indicadores` (supervisores y
 * regionales) NO la ven.
 */
export const ROLES_DISPOSITIVOS: UserRole[] = [UserRole.SUPERADMIN, UserRole.ADMIN, UserRole.MANAGER];

/**
 * Quién cierra la sesión de un equipo y lo reactiva: sólo TI. Cerrar deja al
 * equipo fuera hasta que TI intervenga; hacer sonar no hace daño, por eso el
 * gerente sí puede sonar y etiquetar.
 */
export const ROLES_CIERRE: UserRole[] = [UserRole.SUPERADMIN, UserRole.ADMIN];

export const puedeVerDispositivos = (rol: UserRole | null | undefined): boolean =>
  !!rol && ROLES_DISPOSITIVOS.includes(rol);

export const puedeCerrarSesion = (rol: UserRole | null | undefined): boolean =>
  !!rol && ROLES_CIERRE.includes(rol);

/** El gerente no elige tienda: la API fuerza la suya. */
export const esGerente = (rol: UserRole | null | undefined): boolean => rol === UserRole.MANAGER;

const ROL_LEGIBLE: Record<string, string> = {
  [UserRole.BRANCH_OFFICE]: 'Vendedor (cuenta de tienda)',
  [UserRole.WAREHOUSE_STAFF]: 'Bodeguero',
  [UserRole.CONSULTA]: 'Consulta',
  [UserRole.MANAGER]: 'Gerente',
  [UserRole.SELLER]: 'Vendedor',
  [UserRole.INDICADORES]: 'Indicadores',
  [UserRole.ADMIN]: 'Administrador',
  [UserRole.SUPERADMIN]: 'Superadmin',
};

export function rolLegible(rol: string | null | undefined): string {
  if (!rol) return '—';
  return ROL_LEGIBLE[rol] ?? rol.replace(/^user\.role\./, '');
}

/* ------------------------------------------------------------- el código -- */

const sinGuiones = (id: string): string => id.replace(/-/g, '').toUpperCase();

/**
 * El código que se muestra de cada equipo («7F3A»).
 *
 * La API manda 4 caracteres; si dos equipos de la MISMA lista coinciden, se
 * amplía a 6 en esos dos (§4.1). La lista es de una sola tienda, que es donde
 * alguien busca el equipo por la etiqueta física.
 */
export function codigosVisibles(lista: Pick<Dispositivo, 'id' | 'codigo'>[]): Record<string, string> {
  const base = (d: Pick<Dispositivo, 'id' | 'codigo'>) => (d.codigo || sinGuiones(d.id).slice(0, 4)).toUpperCase();
  const cuenta = new Map<string, number>();
  lista.forEach((d) => cuenta.set(base(d), (cuenta.get(base(d)) ?? 0) + 1));
  const out: Record<string, string> = {};
  lista.forEach((d) => {
    out[d.id] = (cuenta.get(base(d)) ?? 0) > 1 ? sinGuiones(d.id).slice(0, 6) : base(d);
  });
  return out;
}

export const nombreEquipo = (codigo: string): string => `Equipo ${codigo}`;

/* ------------------------------------------------------------ el tiempo -- */

const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

const instante = (iso: string | null | undefined): number | null => {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isFinite(t) ? t : null;
};

/** «10-oct», en hora de México. */
export function diaMesMx(iso: string | null | undefined): string {
  const t = instante(iso);
  if (t === null) return '—';
  try {
    const p = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Mexico_City',
      month: 'numeric',
      day: 'numeric',
    }).formatToParts(new Date(t));
    const x = (tipo: string) => p.find((q) => q.type === tipo)?.value ?? '';
    return `${Number(x('day'))}-${MESES[Number(x('month')) - 1]}`;
  } catch {
    const d = new Date(t);
    return `${d.getDate()}-${MESES[d.getMonth()]}`;
  }
}

/** «hace 40 s», «hace 12 min», «hace 3 h», «hace 2 días»; de una semana para atrás, «el 10-oct». */
export function haceCuanto(iso: string | null | undefined, ahora: number = Date.now()): string {
  const t = instante(iso);
  if (t === null) return '—';
  const s = Math.max(0, Math.round((ahora - t) / 1000));
  if (s < 60) return `hace ${s} s`;
  const min = Math.floor(s / 60);
  if (min < 60) return `hace ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `hace ${h} h`;
  const dias = Math.floor(h / 24);
  if (dias < 7) return dias === 1 ? 'hace 1 día' : `hace ${dias} días`;
  return `el ${diaMesMx(iso)}`;
}

/** «hoy 10:42», «ayer 18:05», «26 sep, 14:05» (el del módulo de metas). */
export const momento = (iso: string | null | undefined, ahora: number = Date.now()): string =>
  momentoMx(iso, ahora) || '—';

/**
 * Si la API arrancó hace poco, cuántos segundos. El registro de conexiones
 * vive en memoria: tras un reinicio dice «0 en pantalla» hasta que los
 * equipos reconectan (segundos, con su *backoff*). Pasados 2 min ya no se avisa.
 */
export function segundosDesdeArranque(iso: string | null | undefined, ahora: number = Date.now()): number | null {
  const t = instante(iso);
  if (t === null) return null;
  const s = Math.max(0, Math.round((ahora - t) / 1000));
  return s <= 120 ? s : null;
}

/* ------------------------------------------------------------ el estado -- */

export const MOTIVOS: { valor: MotivoCierre; etiqueta: string }[] = [
  { valor: 'perdido', etiqueta: 'Perdido' },
  { valor: 'robado', etiqueta: 'Robado' },
  { valor: 'baja', etiqueta: 'Dado de baja' },
  { valor: 'otro', etiqueta: 'Otro' },
];

export const motivoLegible = (m: string | null | undefined): string =>
  MOTIVOS.find((x) => x.valor === m)?.etiqueta.toLowerCase() ?? (m || '—');

export type TonoEstado = 'verde' | 'ambar' | 'gris' | 'rojo';

export const TONO_ESTADO: Record<EstadoDispositivo, TonoEstado> = {
  en_pantalla: 'verde',
  conexion_abierta: 'ambar',
  desconectado: 'gris',
  revocado: 'rojo',
};

/** El texto del estado (§7.3): «En pantalla ahora», «Visto hace 3 h», «Revocado el 10-oct por …». */
export function textoEstado(d: Dispositivo, ahora: number = Date.now()): string {
  switch (d.estado) {
    case 'en_pantalla':
      return 'En pantalla ahora';
    case 'conexion_abierta':
      return 'Conexión abierta, sin actividad';
    case 'revocado': {
      const r = d.revocado;
      if (!r) return 'Revocado';
      return `Revocado el ${diaMesMx(r.en)}${r.por ? ` por ${r.por}` : ''}`;
    }
    default:
      return `Visto ${haceCuanto(d.ultimaVez, ahora)}`;
  }
}

/** ¿Se le puede hacer sonar? Con `desconectado`, el porqué en lugar del botón (§5.1). */
export function sonarDisponible(d: Dispositivo): { ok: boolean; porque?: string } {
  if (d.estado === 'en_pantalla' || d.estado === 'conexion_abierta') return { ok: true };
  if (d.estado === 'revocado') return { ok: false, porque: 'Revocado: su sesión está cerrada.' };
  return { ok: false, porque: 'Desconectado: sólo suena con la app abierta y conectada.' };
}

/* ------------------------------------------------------------- el sonar -- */

export const TEXTO_RESULTADO: Record<ResultadoSonar, string> = {
  sono: 'Sonó',
  bloqueado: 'No sonó: el navegador bloqueó el audio (nadie tocó la pantalla desde que se cargó la app). Sólo se vio el aviso',
  sin_audio: 'No sonó: el equipo no tiene audio. Sólo se vio el aviso',
};

/** Para el renglón «Último sonar», que vive en una columna angosta. El porqué largo está en la «i». */
export const TEXTO_RESULTADO_CORTO: Record<ResultadoSonar, string> = {
  sono: 'sonó',
  bloqueado: 'no sonó (el navegador bloqueó el audio)',
  sin_audio: 'no sonó (el equipo no tiene audio)',
};

export function textoUltimoSonar(u: UltimoSonar | null | undefined, ahora: number = Date.now()): string | null {
  if (!u) return null;
  const resultado = u.resultado ? TEXTO_RESULTADO_CORTO[u.resultado] : 'sin confirmación del equipo';
  return `Último sonar: ${momento(u.en, ahora)} · ${resultado}${u.por ? ` · por ${u.por}` : ''}`;
}

/**
 * Lo que pasó con el último «Hacer sonar» pulsado EN ESTA PANTALLA. La
 * confirmación del equipo (`sono`, `bloqueado`, `sin_audio`) llega después,
 * en `ultimoSonar` de la lista.
 */
export type SonarLocal =
  | { tipo: 'enviando' }
  | { tipo: 'enviado'; sockets: number; enviadoEn: number; previo: string | null }
  | { tipo: 'no_entregado' }
  | { tipo: 'error'; mensaje: string; limite: boolean };

/** Tras este tiempo sin confirmación se dice por qué pudo no sonar. */
export const ESPERA_CONFIRMACION_MS = 30_000;

export interface TextoSonar {
  texto: string;
  tono: 'info' | 'ok' | 'aviso' | 'error';
}

export function textoSonar(
  local: SonarLocal | undefined,
  ultimo: UltimoSonar | null | undefined,
  ahora: number = Date.now(),
): TextoSonar | null {
  if (local?.tipo === 'enviando') return { texto: 'Enviando el aviso…', tono: 'info' };
  if (local?.tipo === 'no_entregado') {
    return {
      texto: 'No llegó: el equipo ya no tiene la app abierta con conexión. El aviso no se guarda para después.',
      tono: 'aviso',
    };
  }
  if (local?.tipo === 'error') return { texto: local.mensaje, tono: local.limite ? 'aviso' : 'error' };
  if (local?.tipo === 'enviado') {
    const confirmado = ultimo && ultimo.en !== local.previo && ultimo.resultado;
    if (confirmado && ultimo?.resultado) {
      return { texto: TEXTO_RESULTADO[ultimo.resultado], tono: ultimo.resultado === 'sono' ? 'ok' : 'aviso' };
    }
    if (ahora - local.enviadoEn > ESPERA_CONFIRMACION_MS) {
      return {
        texto:
          'El equipo no confirmó. Si tiene la pantalla apagada, el aviso se procesa cuando la enciendan.',
        tono: 'aviso',
      };
    }
    const conexiones = local.sockets === 1 ? '1 conexión' : `${local.sockets} conexiones`;
    return { texto: `Aviso enviado (${conexiones}). Esperando a que el equipo confirme…`, tono: 'info' };
  }
  const previo = textoUltimoSonar(ultimo, ahora);
  return previo ? { texto: previo, tono: 'info' } : null;
}

/* ------------------------------------------------------- lista y resumen -- */

/** Revocados al final; los demás, el visto más reciente primero (§4.6). */
export function ordenar(lista: Dispositivo[]): Dispositivo[] {
  return [...lista].sort((a, b) => {
    const ra = a.estado === 'revocado' ? 1 : 0;
    const rb = b.estado === 'revocado' ? 1 : 0;
    if (ra !== rb) return ra - rb;
    return (instante(b.ultimaVez) ?? 0) - (instante(a.ultimaVez) ?? 0);
  });
}

const normal = (s: string | null | undefined): string =>
  (s ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim();

/**
 * Búsqueda por código («7f3a», «Equipo 7F3A»), etiqueta, último vendedor o
 * cuenta: lo que TI tiene a mano cuando la tienda llama (§12, paso 1).
 */
export function filtrar(lista: Dispositivo[], codigos: Record<string, string>, consulta: string): Dispositivo[] {
  const q = normal(consulta).replace(/^equipo\s+/, '');
  if (!q) return lista;
  return lista.filter((d) =>
    [codigos[d.id] ?? d.codigo, d.codigo, d.etiqueta, d.ultimoVendedor?.nombre, d.cuenta?.email].some((v) =>
      normal(v).includes(q),
    ),
  );
}

export interface ChipResumen {
  clave: keyof ResumenDispositivos;
  texto: string;
  tono: 'neutro' | TonoEstado | 'azul';
}

const plural = (n: number, uno: string, varios: string) => (n === 1 ? `1 ${uno}` : `${n} ${varios}`);

/** «5 equipos vistos en los últimos 7 días · 3 en pantalla ahora · …» (§7.3). */
export function chipsResumen(r: ResumenDispositivos, dias: number): ChipResumen[] {
  return [
    { clave: 'vistos', texto: `${plural(r.vistos, 'equipo visto', 'equipos vistos')} en los últimos ${dias} días`, tono: 'neutro' },
    { clave: 'enPantalla', texto: `${r.enPantalla} en pantalla ahora`, tono: 'verde' },
    { clave: 'conexionAbierta', texto: `${r.conexionAbierta} con conexión abierta`, tono: 'ambar' },
    { clave: 'revocados', texto: plural(r.revocados, 'revocado', 'revocados'), tono: 'rojo' },
    { clave: 'nuevos24h', texto: `${plural(r.nuevos24h, 'nuevo', 'nuevos')} en 24 h`, tono: 'azul' },
  ];
}

/** El navegador y el modelo, en una línea. */
export function textoNavegador(d: Pick<Dispositivo, 'navegador' | 'modelo'>): string {
  const partes = [d.navegador, d.modelo].filter((x): x is string => !!x && x.trim() !== '');
  return partes.length ? partes.join(' · ') : '—';
}

export const textoVersion = (v: string | null | undefined): string =>
  !v ? '—' : v === 'sin-version' ? 'sin versión' : `v${v}`;
